import React from 'react';
import {useTranslation} from 'react-i18next';

import CheckedIcon from '@edna/icons/checked.svg';

import {Icon, Stack} from '../primitives';
import {EStackDirection} from '../types';
import * as S from './style';

type TDefaultValue = string | number;

type TStep<Value = TDefaultValue> = {
  id: Value;
  label?: string | null;
  disabled?: boolean;
  completed?: boolean;
};

type TStepProps<Value = TDefaultValue> = {
  index: number;
  step: TStep<Value>;
  isActive: boolean;
  onClick: (id: Value) => void;
};

const Step = React.memo<TStepProps>(
  ({step: {id, label = null, disabled, completed}, isActive, index, onClick}) => {
    const {t} = useTranslation();

    const handleClick = () => {
      if (!disabled) {
        onClick(id);
      }
    };

    const stepCount = completed && !isActive ? +id : undefined;

    return (
      <S.Step disabled={disabled} onClick={handleClick}>
        {label === null && t('EdnaStepper:step', {count: stepCount})}
        {completed && !isActive ? (
          <S.CompletedStep disabled={disabled}>
            <Icon as={CheckedIcon} size="10px" />
          </S.CompletedStep>
        ) : (
          <S.Number isActive={isActive}>{index + 1}</S.Number>
        )}
        {label !== null && label}
      </S.Step>
    );
  },
);

Step.displayName = 'Step';

type TProps<Value = TDefaultValue> = {
  className?: string;
  activeStep: Value;
  steps: TStep<Value>[];
  onClick: (id: Value) => void;
  dataSelector?: string;
};

const Stepper = React.memo<TProps>(({steps, activeStep, onClick, className, dataSelector}) => (
  <Stack
    size="2"
    direction={EStackDirection.ROW}
    justify="space-between"
    align="center"
    className={className}
    data-selector={dataSelector}
  >
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <Step isActive={step.id === activeStep} onClick={onClick} index={index} step={step} />
        {index !== steps.length - 1 && <S.Divider color={step.disabled ? 'grey400' : 'grey900'} />}
      </React.Fragment>
    ))}
  </Stack>
));

export type {TStep, TProps};
export {Stepper};
